import React, { useState, useEffect, useCallback } from 'react';
import {
  getMonthlyCommentTitles,
  getCommentPhaseDistributionForPeriod,
  getPosterInfo,
} from '../firebase/api';
import { useNavigate, useLocation } from 'react-router-dom';
import './PosterList.css';
import './PosterSummary.css';
import './CommentSummary.css';
import { useAuth } from '../firebase/AuthContext';
import { clearPosterListScrollPosition } from '../utils/scrollNavigation';

/** ai_try grant_title_for_comments / 一覧思考フェーズと同じ並び */
const PHASE_TAG_ORDER = [
  'reaction',
  'fact',
  'discovery',
  'grope',
  'reflection',
  'question',
  'analysis',
  'opinion',
  'outcome',
  'insight',
  'intention',
  'trial-and-error',
  'idea',
];

const phaseOrderIndex = (tag) => {
  const i = PHASE_TAG_ORDER.indexOf(tag);
  return i === -1 ? 999 : i;
};

/** 数が多いタグから順に、各タグを count 個だけ並べたフラット配列（モザイク用） */
const buildMosaicCells = (counts) => {
  if (!counts || typeof counts !== 'object') return [];
  const entries = PHASE_TAG_ORDER.map((tag) => ({
    tag,
    count: Math.max(0, Number(counts[tag]) || 0),
  })).filter((e) => e.count > 0);
  entries.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count;
    return phaseOrderIndex(a.tag) - phaseOrderIndex(b.tag);
  });
  const cells = [];
  entries.forEach(({ tag, count }) => {
    for (let i = 0; i < count; i += 1) {
      cells.push({ tag, key: `${tag}-${i}` });
    }
  });
  return cells;
};

const CommentSummary = () => {
  const { currentUser, userProfile, logout, sessionId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(() => {
    if (location.pathname === '/comment-summary' || location.pathname === '/poster-summary') return 'summary';
    if (location.pathname === '/list' || location.pathname === '/poster-list') return 'list';
    if (location.pathname === '/top') return 'poster';
    return 'summary';
  });

  const [monthlyTitles, setMonthlyTitles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [monthlyTitle, setMonthlyTitle] = useState(null);
  const [mosaicCells, setMosaicCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTitles, setLoadingTitles] = useState(true);
  /** poster_id -> サムネイル表示用（get_poster 応答の一部）。null は取得失敗 */
  const [posterThumbById, setPosterThumbById] = useState({});
  const [posterThumbsLoading, setPosterThumbsLoading] = useState(false);

  const handleEditProfile = () => {
    navigate('/profile-setup', { state: { editMode: true } });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'poster') navigate('/top');
    if (tab === 'list') {
      clearPosterListScrollPosition();
      navigate('/list');
    }
    if (tab === 'summary') navigate('/comment-summary');
  };

  const parseYearMonth = (yyyymm) => {
    if (yyyymm === undefined || yyyymm === null) return null;
    const str = String(yyyymm);
    if (str.length !== 6) return null;
    const year = parseInt(str.substring(0, 4), 10);
    const month = parseInt(str.substring(4, 6), 10);
    return { year, month };
  };

  const getPeriodTitle = () => {
    if (!monthlyTitle) return '期間未設定';
    const to = monthlyTitle.to_year_month ? parseYearMonth(monthlyTitle.to_year_month) : null;
    if (!to) return '期間未設定';
    if (monthlyTitle.from_year_month && monthlyTitle.to_year_month) {
      const from = parseYearMonth(monthlyTitle.from_year_month);
      if (from) return `${from.year}年${from.month}月から${to.year}年${to.month}月`;
    }
    if (!monthlyTitle.from_year_month && to) {
      return `${to.year}年${to.month}月まで`;
    }
    return '期間未設定';
  };

  const fetchMonthlyCommentTitles = useCallback(async () => {
    setLoadingTitles(true);
    try {
      if (!sessionId || !currentUser?.uid) {
        setMonthlyTitles([]);
        setLoadingTitles(false);
        return;
      }
      const all = await getMonthlyCommentTitles(sessionId);
      if (!Array.isArray(all)) {
        setMonthlyTitles([]);
        setLoadingTitles(false);
        return;
      }
      const uid = String(currentUser.uid);
      const userRows = all.filter((row) => row && String(row.user_id) === uid);
      userRows.sort((a, b) => {
        const af = a.from_year_month || 0;
        const bf = b.from_year_month || 0;
        return af - bf;
      });
      setMonthlyTitles(userRows);
      if (userRows.length > 0) setCurrentIndex(0);
      setLoadingTitles(false);
    } catch (e) {
      console.error(e);
      setMonthlyTitles([]);
      setLoadingTitles(false);
    }
  }, [sessionId, currentUser?.uid]);

  useEffect(() => {
    if (currentUser && userProfile) fetchMonthlyCommentTitles();
  }, [currentUser, userProfile, fetchMonthlyCommentTitles]);

  useEffect(() => {
    if (monthlyTitles.length > 0 && currentIndex < monthlyTitles.length) {
      setMonthlyTitle(monthlyTitles[currentIndex]);
    } else {
      setMonthlyTitle(null);
    }
  }, [monthlyTitles, currentIndex]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!sessionId || !monthlyTitle?.to_year_month) {
        setMosaicCells([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await getCommentPhaseDistributionForPeriod(sessionId, {
          from_year_month: monthlyTitle.from_year_month ?? null,
          to_year_month: monthlyTitle.to_year_month,
        });
        if (cancelled) return;
        setMosaicCells(buildMosaicCells(res.counts));
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setMosaicCells([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [sessionId, monthlyTitle]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const selections = monthlyTitle?.best_selections;
      if (!sessionId || !Array.isArray(selections)) {
        setPosterThumbById({});
        setPosterThumbsLoading(false);
        return;
      }
      const ids = [
        ...new Set(
          selections.map((s) => (s && s.poster_id ? String(s.poster_id) : null)).filter(Boolean)
        ),
      ];
      if (ids.length === 0) {
        setPosterThumbById({});
        setPosterThumbsLoading(false);
        return;
      }
      setPosterThumbById({});
      setPosterThumbsLoading(true);
      const map = {};
      await Promise.all(
        ids.map(async (pid) => {
          try {
            const info = await getPosterInfo(sessionId, pid);
            map[pid] = {
              imageUrl: info.imageUrl || info.posterImageUrl || '',
              title: info.title || '',
              question: info.question || info.question_title || '',
              authorIconUrl: info.authorIconUrl,
              authorName: info.authorName,
              phase_tags: info.phase_tags || [],
            };
          } catch (e) {
            console.error(e);
            map[pid] = null;
          }
        })
      );
      if (!cancelled) {
        setPosterThumbById(map);
        setPosterThumbsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [sessionId, monthlyTitle]);

  const handlePhaseImageError = (e) => {
    e.target.src = '/img/placeholder.png';
    e.target.onerror = null;
  };

  const handlePosterThumbnailError = (e) => {
    e.target.src = '/img/placeholder.png';
    e.target.style.filter = 'grayscale(100%)';
    e.target.style.opacity = '0.5';
    e.target.onerror = null;
  };

  const getCategoryIcon = (selection) => {
    if (selection.tag_name) {
      return `/img/phase-tags/${selection.tag_name}.png`;
    }
    const iconMap = {
      至高のアイデア: '/img/cat-idea.png',
      至高の分析: '/img/cat-analysis.png',
      至高の洞察: '/img/cat-insight.png',
      至高の余韻: '/img/cat-afterglow.png',
    };
    return iconMap[selection.category] || '/img/cat.png';
  };

  const openPosterForSelection = useCallback(
    async (selection) => {
      const pid = selection.poster_id;
      const did = selection.doc_id;
      if (!sessionId || !pid) {
        window.alert('ポスター情報がありません（古いデータの可能性があります）。');
        return;
      }
      let info = posterThumbById[String(pid)];
      try {
        if (info == null) {
          const raw = await getPosterInfo(sessionId, pid);
          info = {
            imageUrl: raw.imageUrl || raw.posterImageUrl || '',
            title: raw.title || '',
            question: raw.question || raw.question_title || '',
            authorIconUrl: raw.authorIconUrl,
            authorName: raw.authorName,
            phase_tags: raw.phase_tags || [],
          };
        }
        navigate('/poster-detail', {
          state: {
            posterId: pid,
            posterImageUrl: info.imageUrl || '',
            title: info.title || '',
            question: info.question || '',
            authorIconUrl: info.authorIconUrl || userProfile?.iconURL || userProfile?.photoURL,
            authorName: info.authorName || userProfile?.displayName || '',
            phase_tags: info.phase_tags || [],
            focusCommentDocId: did || undefined,
          },
        });
      } catch (e) {
        console.error(e);
        window.alert('ポスター情報の取得に失敗しました。');
      }
    },
    [sessionId, posterThumbById, navigate, userProfile]
  );

  return (
    <div className="list-container">
      <div className="top-header">
        <div className="user-info">
          <img
            src={userProfile?.iconURL || userProfile?.photoURL || '/img/icon.png'}
            alt="プロフィール画像"
            title="プロフィール編集"
            onClick={handleEditProfile}
            style={{ cursor: 'pointer' }}
            onError={(e) => {
              e.target.src = '/img/icon.png';
            }}
          />
          <span className="user-name">
            {userProfile?.displayName || userProfile?.nickname || 'ユーザー'} さん
          </span>
          {userProfile?.world && <span className="user-world">{userProfile.world}</span>}
        </div>
        <div className="header-icons">
          <img
            src="/img/graph.png"
            alt="みんなのサマリー"
            title="みんなのサマリー"
            className="header-icon"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer' }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <img
            src="/img/logout.png"
            alt="ログアウト"
            title="ログアウト"
            className="header-icon"
            onClick={() => { if (window.confirm('ログアウトしますか？')) logout(); }}
            style={{ cursor: 'pointer', marginLeft: '12px' }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      </div>

      <div className="list-tab-menu-wide">
        <button
          type="button"
          className={`list-tab-wide ${activeTab === 'poster' ? 'active' : ''}`}
          onClick={() => handleTabChange('poster')}
        >
          ポスターを作る
        </button>
        <button
          type="button"
          className={`list-tab-wide ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => handleTabChange('list')}
        >
          ポスター一覧
        </button>
        <button
          type="button"
          className={`list-tab-wide ${activeTab === 'summary' ? 'active' : ''}`}
          onClick={() => handleTabChange('summary')}
        >
          サマリー
        </button>
      </div>

      {loadingTitles || (loading && monthlyTitle) ? (
        <div className="summary-loading">読み込み中...</div>
      ) : (
        <div className="summary-container">
          <div className="summary-period-selector">
            <button
              type="button"
              className="summary-period-arrow"
              onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
              disabled={currentIndex === 0 || monthlyTitles.length === 0}
            >
              ←
            </button>
            <h2 className="summary-period-title">{getPeriodTitle()}</h2>
            <button
              type="button"
              className="summary-period-arrow"
              onClick={() => setCurrentIndex((i) => Math.min(monthlyTitles.length - 1, i + 1))}
              disabled={currentIndex >= monthlyTitles.length - 1 || monthlyTitles.length === 0}
            >
              →
            </button>
          </div>

          {monthlyTitle && mosaicCells.length > 0 && (
            <div className="comment-summary-mosaic-wrap">
              <div className="comment-summary-phase-mosaic" aria-label="期間内コメントの思考フェーズ内訳">
                {mosaicCells.map((cell, idx) => {
                  const tagChanged = idx > 0 && mosaicCells[idx - 1].tag !== cell.tag;
                  return (
                    <React.Fragment key={cell.key}>
                      {tagChanged ? (
                        <span className="comment-summary-mosaic-row-break" aria-hidden />
                      ) : null}
                      <img
                        src={`/img/phase-tags/${cell.tag}.png`}
                        alt=""
                        className="comment-summary-mosaic-img"
                        onError={handlePhaseImageError}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {monthlyTitle && mosaicCells.length === 0 && !loading && (
            <div className="comment-summary-mosaic-empty">
              この期間に phase_tags が付いたコメントがまだありません（またはコメントがありません）。
            </div>
          )}

          {(() => {
            const valid =
              monthlyTitle?.best_selections?.filter(
                (s) => s && (s.doc_id || s.content)
              ) || [];
            if (valid.length === 0) return null;
            return (
              <div className="summary-best-selections">
                {valid.map((selection, idx) => (
                  <div key={idx} className="summary-selection-card">
                    <div className="summary-selection-header">
                      <h3 className="summary-selection-title">{selection.category}</h3>
                      <img
                        src={getCategoryIcon(selection)}
                        alt={selection.tag_name || selection.category}
                        className="summary-category-icon"
                        onError={handlePhaseImageError}
                      />
                    </div>
                    <div className="summary-selection-body comment-summary-selection-body">
                      {selection.poster_id && (
                        <div className="summary-selection-posters">
                          {posterThumbsLoading && posterThumbById[String(selection.poster_id)] === undefined ? (
                            <div
                              className="comment-summary-poster-thumb-placeholder"
                              aria-hidden
                            />
                          ) : posterThumbById[String(selection.poster_id)] === null ? (
                            <button
                              type="button"
                              className="comment-summary-poster-thumb-fallback"
                              onClick={() => openPosterForSelection(selection)}
                            >
                              画像を取得できませんでした。タップで開く
                            </button>
                          ) : (
                            <img
                              src={
                                posterThumbById[String(selection.poster_id)]?.imageUrl ||
                                '/img/placeholder.png'
                              }
                              alt={posterThumbById[String(selection.poster_id)]?.title || 'ポスター'}
                              className="summary-poster-thumbnail"
                              onError={handlePosterThumbnailError}
                              onClick={() => openPosterForSelection(selection)}
                            />
                          )}
                        </div>
                      )}
                      {selection.content && (
                        <div className="summary-selection-content">{selection.content}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {!loadingTitles &&
            !monthlyTitle &&
            monthlyTitles.length === 0 &&
            mosaicCells.length === 0 && (
              <div className="summary-no-data">コメント至高のサマリーデータがありません</div>
            )}
        </div>
      )}
    </div>
  );
};

export default CommentSummary;
